// Stillhouse — control-plane brand kit for MoonShine.
// Sibling of the apothecary system: same ink + distillate glow, new building mark.
// Ported verbatim from the Stillhouse design system (window.SH).
import React, { useState, useEffect } from "react";
import { C, F } from "./tokens";
import { VialIcon } from "./apo";

// Self-inject the conveyor keyframes so the loader animates anywhere it's
// mounted — not just on pages whose <style> happens to define them.
(function injectConveyorCSS() {
  if (typeof document === "undefined" || document.getElementById("sh-conveyor-kf")) return;
  const s = document.createElement("style");
  s.id = "sh-conveyor-kf";
  s.textContent = `
    .sh-belt-wrap { -webkit-mask-image: linear-gradient(90deg, transparent, #000 14%, #000 86%, transparent); mask-image: linear-gradient(90deg, transparent, #000 14%, #000 86%, transparent); }
    .sh-belt-surface { background-image: repeating-linear-gradient(90deg, ${C.line} 0 2px, transparent 2px 16px); animation: sh-tread 0.6s linear infinite; }
    @keyframes sh-tread { to { background-position-x: -16px; } }
    .sh-belt-track { animation: sh-belt-run 1.05s linear infinite; }
    @keyframes sh-belt-run { from { transform: translateX(0); } to { transform: translateX(-90px); } }
    .sh-jar { animation: sh-jar-bob 1.05s ease-in-out infinite; }
    .sh-jar:nth-child(2n) { animation-delay: -0.5s; }
    @keyframes sh-jar-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
    @media (prefers-reduced-motion: reduce) { .sh-belt-track, .sh-belt-surface, .sh-jar { animation: none !important; } }
  `;
  document.head.appendChild(s);
})();

// copper — the still's metal. A warm secondary that ties to "stillhouse"
// without competing with the glow. Reuses the amber token's hue, deepened.
export const COPPER = "#E08A4B";

// ── Marks ──────────────────────────────────────────────────────

// The Stillhouse mark — a mono-pitch industrial shed (Shed Works).
export function StillhouseIcon({
  size = 28,
  color = C.glow,
  ink = C.fgDark,
  strokeWidth = 1.7,
}: {
  size?: number;
  color?: string;
  ink?: string;
  strokeWidth?: number;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-label="Stillhouse">
      {/* distillate vapor */}
      <circle cx="13" cy="2.8" r="1.5" fill={color} opacity="0.82" />
      {/* squat stack */}
      <path d="M10.5 16 L10.5 6 L15 6 L15 16" stroke={ink} strokeWidth={strokeWidth} strokeLinejoin="round" />
      <line x1="10.5" y1="9.3" x2="15" y2="9.3" stroke={ink} strokeWidth={strokeWidth} />
      {/* mono-pitch body — high left, low right */}
      <path d="M6 42 L6 16 L42 23 L42 42 Z" stroke={ink} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
      {/* ledge */}
      <line x1="6" y1="28" x2="42" y2="28" stroke={ink} strokeWidth={strokeWidth} />
      {/* glowing bay door */}
      <rect x="27.5" y="31" width="8" height="11" fill={color} opacity="0.88" />
      <rect x="27.5" y="31" width="8" height="11" stroke={ink} strokeWidth="1" />
      {/* windows */}
      {[10, 16.5].map((x) => (
        <g key={x}>
          <rect x={x} y="32" width="4.5" height="5" fill={color} opacity="0.62" />
          <rect x={x} y="32" width="4.5" height="5" stroke={ink} strokeWidth="0.85" />
        </g>
      ))}
    </svg>
  );
}

export function StillhouseWordmark({ size = 22, color, dotColor = C.glow }: { size?: number; color?: string; dotColor?: string }) {
  return (
    <span
      style={{
        fontFamily: F.display,
        fontWeight: 500,
        fontSize: size,
        color: color || "inherit",
        letterSpacing: "-0.025em",
        whiteSpace: "nowrap",
      }}
    >
      stillhouse<span style={{ color: dotColor }}>.</span>
    </span>
  );
}

export function StillhouseLogo({
  wordmarkSize = 21,
  iconSize = 28,
  color,
  dotColor = C.glow,
  ink = C.fgDark,
}: {
  wordmarkSize?: number;
  iconSize?: number;
  color?: string;
  dotColor?: string;
  ink?: string;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 11 }}>
      <StillhouseIcon size={iconSize} color={dotColor} ink={ink} />
      <StillhouseWordmark size={wordmarkSize} color={color} dotColor={dotColor} />
    </span>
  );
}

// ── Conveyor loader ────────────────────────────────────────────
// A bottling line: MoonShine jars riding a moving belt while the
// stillhouse "primes". Auto-advances, click to skip.
export function ConveyorLoader({ onDone }: { onDone?: () => void }) {
  const phases = ["PRIMING THE STILL", "WARMING THE COLUMN", "CALIBRATING METERS", "BOTTLING THE FLEET", "OPENING STILLHOUSE"];
  const [pct, setPct] = useState(0);
  const dur = 2600;
  useEffect(() => {
    const t0 = Date.now();
    let raf: number;
    const tick = () => {
      const p = Math.min(1, (Date.now() - t0) / dur);
      setPct(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => onDone && onDone(), 180);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const phase = Math.min(phases.length - 1, Math.floor(pct * phases.length));
  const jars = [0, 1, 2, 3, 4, 5, 6, 7];

  return (
    <div
      onClick={() => onDone && onDone()}
      style={{
        position: "fixed",
        inset: 0,
        background: C.ink,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontFamily: F.ui,
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(${C.line} 1px, transparent 1px), linear-gradient(90deg, ${C.line} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
          opacity: 0.35,
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          top: "38%",
          transform: "translate(-50%,-50%)",
          width: 620,
          height: 620,
          borderRadius: "50%",
          background: `radial-gradient(closest-side, ${C.glow}14, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <StillhouseLogo wordmarkSize={30} iconSize={42} color={C.fgDark} />

        {/* conveyor */}
        <div className="sh-belt-wrap" style={{ marginTop: 54, width: 480, height: 120, position: "relative" }}>
          {/* belt surface */}
          <div
            className="sh-belt-surface"
            style={{
              position: "absolute",
              left: -30,
              right: -30,
              bottom: 18,
              height: 22,
              background: C.surfaceHi,
              borderTop: `1px solid ${C.lineHi}`,
              borderBottom: `1px solid ${C.line}`,
            }}
          />
          {/* rollers */}
          {[6, 444].map((x) => (
            <div
              key={x}
              style={{
                position: "absolute",
                bottom: 8,
                left: x,
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: C.surface,
                border: `2px solid ${C.lineHi}`,
                boxShadow: `inset 0 0 0 4px ${C.ink}`,
              }}
            />
          ))}
          {/* jars riding the belt — fixed 90px cells so the -90px loop is seamless */}
          <div className="sh-belt-track" style={{ position: "absolute", bottom: 38, left: -90, display: "flex" }}>
            {jars.map((i) => (
              <div key={i} className="sh-jar" style={{ width: 90, display: "flex", justifyContent: "center" }}>
                <VialIcon size={40} color={C.glow} ink={C.fgDark} />
              </div>
            ))}
          </div>
        </div>

        {/* status + progress */}
        <div
          style={{
            marginTop: 30,
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: F.mono,
            fontSize: 11,
            letterSpacing: "0.22em",
            color: C.glow,
            fontWeight: 700,
          }}
        >
          <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: C.glow, boxShadow: `0 0 8px ${C.glow}` }} />
          {phases[phase]}
        </div>
        <div style={{ marginTop: 16, width: 320, height: 3, background: C.surfaceHi, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct * 100}%`, background: C.glow, boxShadow: `0 0 10px ${C.glow}aa` }} />
        </div>
        <div style={{ marginTop: 14, fontFamily: F.mono, fontSize: 10, letterSpacing: "0.18em", color: C.fgFaint }}>
          STILLHOUSE · CONTROL PLANE FOR MOONSHINE · CLICK TO SKIP
        </div>
      </div>
    </div>
  );
}

// ── Shared atoms ───────────────────────────────────────────────

export function Card({
  title,
  tag,
  action,
  children,
  pad = true,
  style,
}: {
  title?: React.ReactNode;
  tag?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  pad?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.line}`, display: "flex", flexDirection: "column", minWidth: 0, ...style }}>
      {(title || action) && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderBottom: `1px solid ${C.line}` }}>
          {title && (
            <span style={{ fontFamily: F.display, fontWeight: 500, fontSize: 17, letterSpacing: "-0.015em", color: C.fgDark }}>{title}</span>
          )}
          {tag}
          <div style={{ flex: 1 }} />
          {action}
        </div>
      )}
      <div style={{ padding: pad ? "16px 18px" : 0, flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

export function SectionLabel({ children, color = C.fgFaint }: { children: React.ReactNode; color?: string }) {
  return (
    <div style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.24em", color, fontWeight: 600, textTransform: "uppercase" }}>{children}</div>
  );
}

// Status semantics shared fleet-wide
export const STATUS: Record<string, [string, string]> = {
  online: [C.glow, "Online"],
  "in-use": [C.lilac, "In use"],
  streaming: [C.glow, "Streaming"],
  offline: [C.fgFaint, "Offline"],
  maintenance: [COPPER, "Maintenance"],
  provisioning: [C.amber, "Provisioning"],
  error: [C.rose, "Error"],
  pending: [C.amber, "Pending"],
  active: [C.glow, "Active"],
  suspended: [C.fgFaint, "Suspended"],
  invited: [C.lilac, "Invited"],
};

export function StatusDot({ status, label, glow }: { status: string; label?: string | false; glow?: boolean }) {
  const [col, defLabel] = STATUS[status] || [C.fgMute, status];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: F.ui, fontSize: 13, color: C.fgDark }}>
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: col,
          boxShadow: glow !== false && col !== C.fgFaint ? `0 0 8px ${col}` : "none",
          flexShrink: 0,
        }}
      />
      {label === false ? null : label || defLabel}
    </span>
  );
}

export function Chip({ children, color = C.fgMute, solid }: { children: React.ReactNode; color?: string; solid?: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        fontFamily: F.mono,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0.06em",
        color: solid ? C.ink : color,
        background: solid ? color : `${color}1c`,
        border: `1px solid ${color}${solid ? "" : "3a"}`,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

const AV_COLORS = [C.glow, C.lilac, COPPER, C.amber, C.rose, "#6BD0FF"];
export function Avatar({ name = "", size = 32 }: { name?: string; size?: number }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const col = AV_COLORS[h % AV_COLORS.length];
  return (
    <span
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        background: `${col}1f`,
        border: `1px solid ${col}55`,
        color: col,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: F.mono,
        fontWeight: 700,
        fontSize: size * 0.36,
        letterSpacing: "0.02em",
      }}
    >
      {initials}
    </span>
  );
}

export function Toggle({
  defaultOn = false,
  on,
  onChange,
  disabled,
}: {
  defaultOn?: boolean;
  on?: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
}) {
  const [internal, setInternal] = useState(defaultOn);
  const isOn = on !== undefined ? on : internal;
  return (
    <button
      onClick={() => {
        if (disabled) return;
        const n = !isOn;
        if (on === undefined) setInternal(n);
        onChange && onChange(n);
      }}
      style={{
        width: 40,
        height: 23,
        borderRadius: 12,
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        background: isOn ? C.glow : C.surfaceHi,
        border: `1px solid ${isOn ? C.glow : C.lineHi}`,
        padding: 0,
        transition: "background .15s, border-color .15s",
        opacity: disabled ? 0.45 : 1,
        boxShadow: isOn ? `0 0 10px ${C.glow}55` : "none",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: isOn ? 19 : 2,
          width: 17,
          height: 17,
          borderRadius: "50%",
          background: isOn ? C.ink : C.fgMute,
          transition: "left .15s",
        }}
      />
    </button>
  );
}

export type TabDef = { id: string; label: string; count?: number | string };
export function Tabs({
  tabs,
  value,
  onChange,
  def,
}: {
  tabs: TabDef[];
  value?: string;
  onChange?: (id: string) => void;
  def?: string;
}) {
  const [internal, setInternal] = useState(def || (tabs[0] && tabs[0].id));
  const active = value !== undefined ? value : internal;
  return (
    <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${C.line}` }}>
      {tabs.map((t) => {
        const on = active === t.id;
        return (
          <button
            key={t.id}
            onClick={() => {
              if (value === undefined) setInternal(t.id);
              onChange && onChange(t.id);
            }}
            style={{
              padding: "10px 16px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: F.ui,
              fontSize: 13.5,
              fontWeight: on ? 600 : 500,
              color: on ? C.fgDark : C.fgMute,
              borderBottom: `2px solid ${on ? C.glow : "transparent"}`,
              marginBottom: -1,
              letterSpacing: "-0.005em",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {t.label}
            {t.count != null && <span style={{ fontFamily: F.mono, fontSize: 10, color: on ? C.glow : C.fgFaint, fontWeight: 700 }}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Simple labelled bar for charts
export function Bar({
  value,
  max = 100,
  color = C.glow,
  height = 8,
  bg = C.surfaceHi,
}: {
  value: number;
  max?: number;
  color?: string;
  height?: number;
  bg?: string;
}) {
  const pct = Math.max(0.02, Math.min(1, value / max));
  return (
    <div style={{ width: "100%", height, background: bg, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct * 100}%`, background: color, boxShadow: `0 0 8px ${color}66` }} />
    </div>
  );
}

// Page header used by every view
export function PageHeader({
  crumb,
  title,
  sub,
  children,
}: {
  crumb: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ padding: "24px 32px 20px", borderBottom: `1px solid ${C.line}`, display: "flex", alignItems: "flex-end", gap: 18 }}>
      <div style={{ minWidth: 0 }}>
        <SectionLabel>{crumb}</SectionLabel>
        <div style={{ fontFamily: F.display, fontWeight: 500, fontSize: 32, marginTop: 6, letterSpacing: "-0.025em", color: C.fgDark, lineHeight: 1.05 }}>
          {title}
        </div>
        {sub && <div style={{ marginTop: 7, fontSize: 14, color: C.fgMute, maxWidth: 720, lineHeight: 1.5 }}>{sub}</div>}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>{children}</div>
    </div>
  );
}

// Muted placeholder shown when a list has no real data yet. Keeps the
// design-system look without fabricating sample rows.
export function EmptyState({ title, sub }: { title: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div style={{ padding: "32px 18px", textAlign: "center", color: C.fgMute }}>
      <div style={{ fontFamily: F.ui, fontSize: 14, color: C.fgDark }}>{title}</div>
      {sub && <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgFaint, marginTop: 8, letterSpacing: "0.04em" }}>{sub}</div>}
    </div>
  );
}

// A definition/stat tile
export function Stat({
  label,
  value,
  sub,
  accent = C.glowDp,
  children,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent?: string;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderTop: `2px solid ${accent}`, padding: "16px 18px", minWidth: 0 }}>
      <SectionLabel>{label}</SectionLabel>
      <div style={{ fontFamily: F.display, fontWeight: 500, fontSize: 38, color: C.fgDark, letterSpacing: "-0.03em", lineHeight: 1.05, marginTop: 6 }}>
        {value}
      </div>
      {sub && <div style={{ fontFamily: F.ui, fontSize: 12.5, color: C.fgMute, marginTop: 6 }}>{sub}</div>}
      {children && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
}
